import { Button } from './ui/button';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { 
  Key,
  AlertTriangle,
  CheckCircle,
  Mail
} from 'lucide-react';
import { useState } from 'react';

interface TeamMember {
  id: number;
  firstname: string;
  lastname: string;
  email: string;
  status: 'Active' | 'Inactive' | 'Pending';
}

interface ResetMemberPasswordProps {
  isOpen: boolean;
  onClose: () => void;
  member: TeamMember | null;
  onSuccess?: () => void;
  resetMemberPassword: (memberId: number) => Promise<any>;
}

export function ResetMemberPassword({ 
  isOpen, 
  onClose, 
  member, 
  onSuccess,
  resetMemberPassword 
}: ResetMemberPasswordProps) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleResetPassword = async () => {
    if (!member) return;

    setIsProcessing(true);
    setError(null);
    
    try {
      const response = await resetMemberPassword(member.id);
      
      if (response.success) {
        setIsProcessing(false);
        setIsComplete(true);
        
        if (onSuccess) {
          onSuccess();
        }
        
        setTimeout(() => {
          onClose();
          setIsComplete(false);
        }, 2500);
      } else {
        throw new Error(response.message || 'Failed to reset password');
      }
    } catch (error) {
      setIsProcessing(false);
      setError(error instanceof Error ? error.message : 'Failed to reset password. Please try again.');
    }
  };

  const handleClose = () => {
    setError(null);
    setIsComplete(false);
    onClose();
  };

  const renderContent = () => {
    if (isComplete) {
      return (
        <div className="text-center py-8">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Password Reset Successfully!
          </h3>
          <p className="text-muted-foreground mb-4">
            A password reset email has been sent to {member?.email}
          </p>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-sm text-green-700">
              <Mail className="w-4 h-4 inline mr-2" />
              The user will receive instructions to create a new password.
            </p>
          </div>
        </div>
      );
    }

    if (isProcessing) {
      return (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-4"></div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Resetting Password...
          </h3>
          <p className="text-muted-foreground">
            Sending password reset email to {member?.firstname} {member?.lastname}...
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="flex items-start gap-4">
          <div className="flex-1">
           
            <p className="text-muted-foreground">
              Are you sure you want to reset the password for <strong>{member?.firstname} {member?.lastname}</strong>?
            </p>
            
           
        
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

          </div>
        </div>
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader className="border-b pb-4">
          <DialogTitle className="flex items-center space-x-2">
            <Key className="w-5 h-5 text-amber-600" />
            <span>Reset Password</span>
          </DialogTitle>
          <DialogDescription>
            This action will send a password reset email to the team member.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4">
          {renderContent()}
        </div>
        
        {!isProcessing && !isComplete && (
          <div className="flex justify-end gap-3 mt-4 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button
              onClick={handleResetPassword}
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              Reset Password
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
